/**
 * Per-recipient message drill-down for a drop (paginated).
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { useToolDropMessages } from '@/hooks/smswiser/tool-stats';
import { formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';

const LIMIT = 100;
const HEAD = ['Phone', 'Sender', 'Status', 'Domain', 'Sent', 'Message'];

interface ToolMessagesPanelProps {
  uniqueId: string;
}

export const ToolMessagesPanel: FC<ToolMessagesPanelProps> = ({ uniqueId }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching } = useToolDropMessages(uniqueId, {
    page,
    limit: LIMIT,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const maxPage = Math.max(1, Math.ceil(total / LIMIT));

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSpinner />
      </div>
    );
  }
  if (isError) {
    return <p className="py-4 text-sm text-destructive">Failed to load messages.</p>;
  }
  if (items.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">No messages for this drop.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              {HEAD.map((h) => (
                <th key={h} className="px-3 py-2 text-left font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr key={m.apiMessageId ?? `${m.phoneNumber}-${i}`} className="border-t border-border">
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{m.phoneNumber}</td>
                <td className="px-3 py-2">{m.sender || '—'}</td>
                <td className="px-3 py-2">{m.smsStatus || '—'}</td>
                <td className="px-3 py-2">{m.domain || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                  {m.sentAt ? formatDateTimeDMYHM(new Date(m.sentAt)) : '—'}
                </td>
                <td className="max-w-[320px] truncate px-3 py-2 text-muted-foreground" title={m.messageContent}>
                  {m.messageContent || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total.toLocaleString()} messages · page {page} / {maxPage}
          {isFetching ? ' · updating…' : ''}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= maxPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ToolMessagesPanel;
